// Taşeron portalı — ortak personel düzenleyicisinin (`usePersonnelEditor`) bağı (#29): kendi
// personel kaydı §13.6 `PUT /supplier/contractor/personnel/{userUuid}`. web-expert
// `personnelClient.ts`'in SP ikizi; SP wire gövdesini kuran TEK yer burasıdır.
//
// `tcKimlikNo` üç durumludur (§13.6, EP ile aynı): anahtar YALNIZ `tc.kind === 'set'` iken konur
// (kullanıcı boş bir T.C.'ye yazdı); `keep`te anahtar hiç yoktur = değişmez. Böylece bayat bir
// liste satırı, başka bir yazarın (ör. yetkili ana firma uzmanı) arada kaydettiği T.C.'yi `null`
// göndererek silemez; maskeli ya da bayat bir değer de asla geri gönderilmez. `null` (temizle) bu
// arayüzden ulaşılmazdır. Ad/soyad `null` = değişmez (kayıtlı ad boş, alan boş bırakıldı).
//
// Ad küresel `SupplierUser`dadır (§13.6 "SupplierUser durumu globaldir"): kaydetmeden sonra kişi
// listesi + kişinin belge öneki + kişiyi gömen HER izin detayı/listesi + pano tazelenir. Hata
// sonrası da liste + izinler + pano (404 / 409 → satır bayat olabilir; çekmece güncel sunucu
// durumunu göstersin).
// Her anahtar `companyId` taşır → firma değişimi öbür firmanın satırlarını önbellekten sunamaz.

import {
  type ContractorPersonnelDto,
  type PersonnelClient,
  supplierContractorApi,
} from '@isgportal/shared';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supplierContractorKeys } from './supplierContractorKeys';
import { supplierDashboardKeys } from './supplierDashboardKeys';
import { supplierPermitKeys } from './supplierPermitKeys';

function invalidatePersonnelRecordCaches(queryClient: QueryClient, companyId: number | null) {
  queryClient.invalidateQueries({ queryKey: supplierContractorKeys.personnel(companyId) });
  // İzin detayları + listeler kişinin adını taşır (adlar globaldir).
  queryClient.invalidateQueries({ queryKey: supplierPermitKeys.all() });
  // Pano "Sizden bekleyenler"/"Eksikler" kalemleri `personnelName`'i canlı çözer (§28.3/§28.7).
  // Firma özeti (`workspacesSummary`) yalnız menü açıkken çekilir → yalnız bayat işaretlenir.
  queryClient.invalidateQueries({ queryKey: supplierDashboardKeys.all() });
}

export function usePersonnelClient(companyId: number | null): PersonnelClient {
  const queryClient = useQueryClient();
  return useMemo<PersonnelClient>(
    () => ({
      save: (userUuid, change) =>
        supplierContractorApi.updateSupplierContractorPersonnel(userUuid, {
          firstName: change.firstName,
          lastName: change.lastName,
          accessLevel: change.accessLevel,
          isActive: change.isActive,
          ...(change.tc.kind === 'set' ? { tcKimlikNo: change.tc.value } : {}),
        }),
      afterSave: (updated) => {
        // Yanıt listeye hemen yazılır → çekmece başlığı ve satır yeniden çekmeyi beklemeden yeni adı
        // gösterir; ardından tazeleme sunucu sırasını/durumunu getirir.
        queryClient.setQueryData<ContractorPersonnelDto[]>(
          supplierContractorKeys.personnel(companyId),
          (prev) => prev?.map((p) => (p.uuid === updated.uuid ? updated : p)),
        );
        invalidatePersonnelRecordCaches(queryClient, companyId);
        queryClient.invalidateQueries({
          queryKey: supplierContractorKeys.personnelByUuid(companyId, updated.uuid),
        });
      },
      afterError: () => invalidatePersonnelRecordCaches(queryClient, companyId),
    }),
    [companyId, queryClient],
  );
}
